// CR151: Simulate Panel for testing policy rules
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Play, CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";
import { useDealPolicy } from "@/hooks/useDealPolicy";
import type { DealPolicyConfig, SimulateInput, SimulateOutput, SourceType, EligibilityScope } from "@/lib/deal-policy/types";

interface SimulatePanelProps {
  config: DealPolicyConfig;
}

const SCOPE_COLORS: Record<EligibilityScope, string> = {
  CONSUMER: "bg-green-100 text-green-800",
  CORPORATE: "bg-blue-100 text-blue-800",
  MEMBERS_ONLY: "bg-purple-100 text-purple-800",
  STUDENT: "bg-yellow-100 text-yellow-800",
  EMPLOYEE: "bg-orange-100 text-orange-800",
  NEW_CUSTOMER: "bg-pink-100 text-pink-800",
  UNKNOWN: "bg-gray-100 text-gray-800",
};

export function SimulatePanel({ config }: SimulatePanelProps) {
  const { simulate } = useDealPolicy();
  const [input, setInput] = useState<SimulateInput>({
    source_type: "WEB",
    headline: "",
    body_snippet: "",
    url: "",
    threshold_cad: undefined,
  });
  const [output, setOutput] = useState<SimulateOutput | null>(null);

  const handleSimulate = () => {
    const result = simulate(input, config);
    setOutput(result);
  };

  const handleClear = () => {
    setInput({
      source_type: "WEB",
      headline: "",
      body_snippet: "",
      url: "",
      threshold_cad: undefined,
    });
    setOutput(null);
  };

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Input Side */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm">Input</h4>
        
        <div className="space-y-2">
          <Label>Source Type</Label>
          <Select
            value={input.source_type}
            onValueChange={(v) => setInput({ ...input, source_type: v as SourceType })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="WEB">WEB</SelectItem>
              <SelectItem value="FLYER">FLYER</SelectItem>
              <SelectItem value="FORUM">FORUM</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Headline *</Label>
          <Textarea
            rows={3}
            value={input.headline}
            onChange={(e) => setInput({ ...input, headline: e.target.value })}
            placeholder="e.g., Get $100 in corporate gift cards when you spend $1,000"
          />
        </div>

        <div className="space-y-2">
          <Label>Body Snippet (optional)</Label>
          <Textarea
            rows={2}
            value={input.body_snippet || ""}
            onChange={(e) => setInput({ ...input, body_snippet: e.target.value })}
            placeholder="Additional text to search for keywords"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>URL (optional)</Label>
            <Input
              value={input.url || ""}
              onChange={(e) => setInput({ ...input, url: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <div className="space-y-2">
            <Label>Threshold CAD (optional)</Label>
            <Input
              type="number"
              value={input.threshold_cad ?? ""}
              onChange={(e) => setInput({ ...input, threshold_cad: e.target.value ? parseFloat(e.target.value) : undefined })}
              placeholder="500"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleSimulate} disabled={!input.headline}>
            <Play className="h-4 w-4 mr-1" />
            Simulate
          </Button>
          <Button variant="outline" onClick={handleClear}>
            Clear
          </Button>
        </div>
      </div>

      {/* Output Side */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm">Output</h4>
        
        {!output ? (
          <div className="border rounded-lg p-8 text-center text-muted-foreground">
            <p>Enter input and click Simulate to see results</p>
          </div>
        ) : (
          <Card>
            <CardContent className="pt-4 space-y-4">
              {/* Eligibility Scope */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Eligibility Scope:</span>
                <Badge className={SCOPE_COLORS[output.eligibility_scope]}>
                  {output.eligibility_scope}
                </Badge>
              </div>

              <Separator />

              {/* Fired Rules */}
              <div className="space-y-2">
                <span className="text-sm font-medium">Fired Rules:</span>
                {output.fired_rule_ids.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No rules matched (defaulting to CONSUMER)</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {output.fired_rule_ids.map(id => (
                      <Badge key={id} variant="outline">{id}</Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Evidence */}
              {output.evidence_snippets.length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm font-medium">Evidence:</span>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {output.evidence_snippets.map((e, i) => (
                      <li key={i} className="font-mono text-xs bg-muted px-2 py-1 rounded">
                        {e}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Separator />

              {/* Issues */}
              <div className="space-y-2">
                <span className="text-sm font-medium">Issues:</span>
                {output.issues.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No issues detected</p>
                ) : (
                  <div className="space-y-1">
                    {output.issues.map((issue, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        {issue.severity === "HARD_BLOCK" && <XCircle className="h-4 w-4 text-destructive" />}
                        {issue.severity === "WARNING" && <AlertTriangle className="h-4 w-4 text-yellow-600" />}
                        {issue.severity === "INFO" && <Info className="h-4 w-4 text-blue-600" />}
                        <span className="font-mono">{issue.issue_key}</span>
                        <span className="text-muted-foreground">- {issue.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Status */}
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  {output.is_dedup_eligible ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                  <span className="text-sm">Dedup Eligible</span>
                </div>
                <div className="flex items-center gap-2">
                  {output.is_publishable ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                  <span className="text-sm">Publishable</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
